export type StackCategory = 'crm' | 'email' | 'ads' | 'seo';

export type CrmProvider = 'pipedrive' | 'boulevard' | 'hubspot' | 'salesforce' | 'none';
export type EmailProvider = 'smartlead' | 'instantly' | 'lemlist' | 'none';
export type AdsProvider = 'google_ads' | 'meta_ads' | 'none';
export type SeoProvider = 'google_search_console' | 'none';

export type StackProvider = CrmProvider | EmailProvider | AdsProvider | SeoProvider;

export type OrgStackEntry = {
  id: string;
  organizationId: string;
  category: StackCategory;
  provider: StackProvider;
  isActive: boolean;
  configuredAt: string;
};

export type OrgStack = Record<StackCategory, OrgStackEntry | null>;

export type ProviderStatus = 'available' | 'coming_soon' | 'manual';

export type ProviderInfo = {
  id: StackProvider;
  label: string;
  category: StackCategory;
  status: ProviderStatus;
  description: string;
};

export const STACK_CATEGORIES: { key: StackCategory; label: string; description: string }[] = [
  { key: 'crm', label: 'CRM / Pipeline', description: 'Track deals, clients, and pipeline' },
  { key: 'email', label: 'Cold Email', description: 'Outbound email campaigns' },
  { key: 'ads', label: 'Paid Ads', description: 'Paid advertising channels' },
  { key: 'seo', label: 'SEO / Organic', description: 'Organic search tracking' },
];

export const PROVIDERS: Record<StackCategory, ProviderInfo[]> = {
  crm: [
    { id: 'pipedrive', label: 'Pipedrive', category: 'crm', status: 'available', description: 'Sales CRM & pipeline management' },
    { id: 'boulevard', label: 'Boulevard', category: 'crm', status: 'coming_soon', description: 'Client management for service businesses' },
    { id: 'hubspot', label: 'HubSpot', category: 'crm', status: 'coming_soon', description: 'All-in-one CRM platform' },
    { id: 'salesforce', label: 'Salesforce', category: 'crm', status: 'coming_soon', description: 'Enterprise CRM' },
    { id: 'none', label: 'Not using a CRM', category: 'crm', status: 'manual', description: 'Skip this category' },
  ],
  email: [
    { id: 'smartlead', label: 'Smartlead', category: 'email', status: 'available', description: 'AI-powered cold email outreach' },
    { id: 'instantly', label: 'Instantly', category: 'email', status: 'coming_soon', description: 'Cold email automation platform' },
    { id: 'lemlist', label: 'Lemlist', category: 'email', status: 'coming_soon', description: 'Personalized cold outreach' },
    { id: 'none', label: 'Not using cold email', category: 'email', status: 'manual', description: 'Skip this category' },
  ],
  ads: [
    { id: 'google_ads', label: 'Google Ads', category: 'ads', status: 'available', description: 'Search & display advertising' },
    { id: 'meta_ads', label: 'Meta Ads', category: 'ads', status: 'coming_soon', description: 'Facebook & Instagram ads' },
    { id: 'none', label: 'No paid ads', category: 'ads', status: 'manual', description: 'Skip this category' },
  ],
  seo: [
    { id: 'google_search_console', label: 'Google Search Console', category: 'seo', status: 'available', description: 'Organic search performance' },
    { id: 'none', label: 'No SEO tracking', category: 'seo', status: 'manual', description: 'Skip this category' },
  ],
};

/** Get the default empty stack */
export function emptyStack(): OrgStack {
  return { crm: null, email: null, ads: null, seo: null };
}
